// Minimal WebRTC + signaling helper for 1:1 calls.
// Caller (host) creates the offer once the peer is present; callee (candidate) answers.

const DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

let cachedIceServers = null;

async function fetchIceServers() {
  if (cachedIceServers) return cachedIceServers;
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${backend}/api/ice-servers`, { credentials: "omit" });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.iceServers) && data.iceServers.length) {
        cachedIceServers = data.iceServers;
        return cachedIceServers;
      }
    }
  } catch {
    // fall through to defaults
  }
  cachedIceServers = DEFAULT_ICE_SERVERS;
  return cachedIceServers;
}

export function wsUrl(roomId, role) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const url = new URL(backend);
  const proto = url.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${url.host}/api/ws/live/${roomId}/${role}`;
}

export class LiveCall {
  constructor({ roomId, role, onRemoteStream, onPeerLeft, onConnectionState, onPeerJoined }) {
    this.roomId = roomId;
    this.role = role; // 'host' | 'candidate'
    this.onRemoteStream = onRemoteStream;
    this.onPeerLeft = onPeerLeft;
    this.onConnectionState = onConnectionState;
    this.onPeerJoined = onPeerJoined;
    this.pc = null;
    this.ws = null;
    this.localStream = null;
    this.remoteStream = new MediaStream();
    this.pendingCandidates = [];
    this.makingOffer = false;
    this.polite = role === "candidate"; // host is impolite (initiator)
  }

  async start(localStream) {
    this.localStream = localStream;
    const iceServers = await fetchIceServers();
    this._setupPC(iceServers);
    localStream.getTracks().forEach((t) => this.pc.addTrack(t, localStream));
    this._connectWS();
  }

  _setupPC(iceServers) {
    this.pc = new RTCPeerConnection({ iceServers });
    this.pc.ontrack = (e) => {
      e.streams[0].getTracks().forEach((t) => {
        if (!this.remoteStream.getTracks().includes(t)) {
          this.remoteStream.addTrack(t);
        }
      });
      this.onRemoteStream && this.onRemoteStream(this.remoteStream);
    };
    this.pc.onicecandidate = (e) => {
      if (e.candidate) {
        this._send({ type: "ice", candidate: e.candidate });
      }
    };
    this.pc.onconnectionstatechange = () => {
      this.onConnectionState && this.onConnectionState(this.pc.connectionState);
    };
    this.pc.onnegotiationneeded = async () => {
      if (this.role !== "host") return;
      try {
        this.makingOffer = true;
        await this.pc.setLocalDescription();
        this._send({ type: "offer", sdp: this.pc.localDescription });
      } catch {
        // ignore
      } finally {
        this.makingOffer = false;
      }
    };
  }

  _connectWS() {
    const url = wsUrl(this.roomId, this.role);
    this.ws = new WebSocket(url);
    this.ws.onmessage = (e) => this._onSignal(JSON.parse(e.data));
    this.ws.onopen = () => {
      // For host: when WS opens, do nothing — wait for peer-joined or peer-present
    };
    this.ws.onclose = () => {};
  }

  async _onSignal(msg) {
    if (!msg) return;
    if (msg.type === "peer-joined" || msg.type === "peer-present") {
      this.onPeerJoined && this.onPeerJoined(msg.type);
      if (this.role === "host") {
        // Trigger negotiation
        try {
          await this.pc.setLocalDescription(await this.pc.createOffer());
          this._send({ type: "offer", sdp: this.pc.localDescription });
        } catch {
          // ignore
        }
      }
      return;
    }
    if (msg.type === "peer-left") {
      this.onPeerLeft && this.onPeerLeft();
      return;
    }
    if (msg.type === "offer") {
      const desc = new RTCSessionDescription(msg.sdp);
      const offerCollision =
        this.makingOffer || this.pc.signalingState !== "stable";
      if (offerCollision && !this.polite) return;
      await this.pc.setRemoteDescription(desc);
      await this.pc.setLocalDescription(await this.pc.createAnswer());
      this._send({ type: "answer", sdp: this.pc.localDescription });
      this._flushIce();
      return;
    }
    if (msg.type === "answer") {
      await this.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      this._flushIce();
      return;
    }
    if (msg.type === "ice") {
      try {
        if (this.pc.remoteDescription && this.pc.remoteDescription.type) {
          await this.pc.addIceCandidate(msg.candidate);
        } else {
          this.pendingCandidates.push(msg.candidate);
        }
      } catch {
        // ignore
      }
    }
  }

  async _flushIce() {
    while (this.pendingCandidates.length) {
      const c = this.pendingCandidates.shift();
      try {
        await this.pc.addIceCandidate(c);
      } catch {
        // ignore
      }
    }
  }

  _send(payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  hangup() {
    try {
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
      }
    } catch {}
    try {
      if (this.pc) this.pc.close();
    } catch {}
    try {
      if (this.ws) this.ws.close();
    } catch {}
  }
}
